import { DateTime } from 'luxon';
import { proxy } from 'valtio';
import { getCurrentRuntimeTarget } from '../../config/runtime-target';

import {
  APP_TIME_ENABLED_KEY,
  APP_TIME_MOCKED_UTC_MS_KEY,
  APP_TIME_TIMEZONE_KEY,
} from './constants';

export interface AppTimeState {
  enabled: boolean;
  mockedUtcMs: number | null;
  timeZoneId: string | null;
}

export const appTimeStore = proxy<AppTimeState>({
  enabled: false,
  mockedUtcMs: null,
  timeZoneId: null,
});

export const isDevApiEnv = (): boolean => getCurrentRuntimeTarget() === 'development';

export const appTimeHasMock = (state: AppTimeState = appTimeStore): boolean =>
  state.enabled && state.mockedUtcMs != null;

/** Effective mocked instant as JS Date (Flutter `AppTimeState.mockedNow`). */
export const getMockedNow = (state: AppTimeState = appTimeStore): Date | null => {
  if (!appTimeHasMock(state) || state.mockedUtcMs == null) {
    return null;
  }
  if (!state.timeZoneId) {
    return new Date(state.mockedUtcMs);
  }
  return DateTime.fromMillis(state.mockedUtcMs, { zone: state.timeZoneId }).toJSDate();
};

const toUtcMs = (local: Date, timeZoneId: string | null): number => {
  if (!timeZoneId) {
    return local.getTime();
  }
  return DateTime.fromObject(
    {
      year: local.getFullYear(),
      month: local.getMonth() + 1,
      day: local.getDate(),
      hour: local.getHours(),
      minute: local.getMinutes(),
      second: local.getSeconds(),
      millisecond: local.getMilliseconds(),
    },
    { zone: timeZoneId },
  ).toMillis();
};

const persist = (state: AppTimeState) => {
  if (!isDevApiEnv()) {
    return;
  }
  try {
    if (!appTimeHasMock(state)) {
      localStorage.removeItem(APP_TIME_ENABLED_KEY);
      localStorage.removeItem(APP_TIME_MOCKED_UTC_MS_KEY);
    } else {
      localStorage.setItem(APP_TIME_ENABLED_KEY, 'true');
      localStorage.setItem(APP_TIME_MOCKED_UTC_MS_KEY, String(state.mockedUtcMs!));
    }
    if (!state.timeZoneId) {
      localStorage.removeItem(APP_TIME_TIMEZONE_KEY);
    } else {
      localStorage.setItem(APP_TIME_TIMEZONE_KEY, state.timeZoneId);
    }
  } catch {
    // ignore quota / private mode
  }
};

const loadFromStorage = (): AppTimeState => {
  if (!isDevApiEnv()) {
    try {
      localStorage.removeItem(APP_TIME_ENABLED_KEY);
      localStorage.removeItem(APP_TIME_MOCKED_UTC_MS_KEY);
      localStorage.removeItem(APP_TIME_TIMEZONE_KEY);
    } catch {
      // ignore
    }
    return { enabled: false, mockedUtcMs: null, timeZoneId: null };
  }

  try {
    const enabled = localStorage.getItem(APP_TIME_ENABLED_KEY) === 'true';
    const rawMs = localStorage.getItem(APP_TIME_MOCKED_UTC_MS_KEY);
    const mockedUtcMs = rawMs && /^\d+$/.test(rawMs) ? Number.parseInt(rawMs, 10) : null;
    const timeZoneId = localStorage.getItem(APP_TIME_TIMEZONE_KEY);
    return {
      enabled: enabled && mockedUtcMs != null,
      mockedUtcMs,
      timeZoneId: timeZoneId || null,
    };
  } catch {
    return { enabled: false, mockedUtcMs: null, timeZoneId: null };
  }
};

export const appTimeActions = {
  init: () => {
    const loaded = loadFromStorage();
    appTimeStore.enabled = loaded.enabled;
    appTimeStore.mockedUtcMs = loaded.mockedUtcMs;
    appTimeStore.timeZoneId = loaded.timeZoneId;
  },

  enableMock: (localDateTime: Date, timeZoneId?: string | null) => {
    if (!isDevApiEnv()) {
      return;
    }
    const nextTz = timeZoneId !== undefined ? timeZoneId : appTimeStore.timeZoneId;
    appTimeStore.enabled = true;
    appTimeStore.mockedUtcMs = toUtcMs(localDateTime, nextTz);
    appTimeStore.timeZoneId = nextTz;
    persist(appTimeStore);
  },

  setTimeZone: (timeZoneId: string | null) => {
    if (!isDevApiEnv()) {
      return;
    }
    appTimeStore.timeZoneId = timeZoneId;
    persist(appTimeStore);
  },

  setEnabled: (value: boolean) => {
    if (!value) {
      appTimeActions.clearMock();
      return;
    }
    if (appTimeStore.mockedUtcMs != null) {
      appTimeStore.enabled = true;
      persist(appTimeStore);
    }
  },

  clearMock: () => {
    appTimeStore.enabled = false;
    appTimeStore.mockedUtcMs = null;
    persist(appTimeStore);
  },
};
